import {Component, OnInit, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ButtonSize, ButtonState, ButtonType, IApplication} from '../../../assets/types/dtoTypes';
import {AppointmentSuccessDialogComponent} from './appointment-success-dialog/appointment-success-dialog.component';
import {TranslateModule} from "@ngx-translate/core";
import {MatFormField, MatInput, MatInputModule, MatLabel} from "@angular/material/input";
import {ButtonComponent} from "../../shared/button/button.component";
import {FormsModule} from "@angular/forms";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerModule,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import {NgForOf, NgIf} from "@angular/common";
import {v4 as uuidv4} from 'uuid';
import {MatNativeDateModule} from "@angular/material/core";


@Component({
  selector: 'app-interview-modal',
  templateUrl: './interview-modal.component.html',
  standalone: true,
  imports: [
    MatLabel,
    TranslateModule,
    ButtonComponent,
    FormsModule,
    MatInput,
    MatInputModule,
    MatFormField,
    MatDatepicker,
    MatDatepickerToggle,
    MatDatepickerInput,
    MatDatepickerModule,
    MatNativeDateModule,
    NgForOf,
    NgIf
  ],
  styleUrls: ['./interview-modal.component.scss']
})
export class InterviewDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<InterviewDialogComponent>>(MatDialogRef);
  data = inject<IApplication>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);

  title: string = '';

  selectedDate: Date | null = null;

  startTime: string = '';

  minDate: Date = new Date();

  endTime: string = '';

  guestsEnabled = false;

  guests = [];

  constructor() {
  }

  confirm(expectGuests: boolean): void {
    if (!this.selectedDate || !this.startTime || !this.endTime) return;

    if (!this.isTimeRangeValid()) return;

    if (expectGuests) {
      const invalidGuest = this.guests.some(g =>
        !g.guestName || !g.guestEmail || !this.isValidEmail(g.guestEmail)
      );
      if (invalidGuest || this.guests.length === 0) return;
    }

    const toHms = (t: string) => {
      const [h='00', m='00', s='00'] = t.split(':');
      return `${h.padStart(2,'0')}:${m.padStart(2,'0')}:${s.padStart(2,'0')}`;
    };

    this.dialogRef.close({
      title: this.title.trim() || 'Interview',
      interviewDate: this.getLocalDateString(this.selectedDate),
      startTime: toHms(this.startTime),
      endTime: toHms(this.endTime),
      guests: expectGuests
        ? this.guests.filter(g => g.guestName && g.guestEmail)
        : [],
    });

    setTimeout(() => {
      this.dialog.open(AppointmentSuccessDialogComponent, {
        data: {
          name: this.data.applicantName,
          date: this.selectedDate!.toDateString(),
          time: this.startTime,
        },
        panelClass: 'success-popup-dialog',
      });
    }, 100);
  }


  isFormComplete(): boolean {
    return !!(this.selectedDate && this.startTime && this.endTime && this.isTimeRangeValid());
  }

  canConfirmWithGuests(): boolean {
    if (!this.isFormComplete()) return false;
    if (!this.guestsEnabled || this.guests.length === 0) return false;

    return this.guests.every(g =>
      g.guestName && g.guestEmail && this.isValidEmail(g.guestEmail)
    );
  }



  private getLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  addGuest() {
    this.guests.push({
      guestName: '',
      guestEmail: '',
      guestId: uuidv4(),
    });
  }

  removeGuest(index: number) {
    this.guests.splice(index, 1);
    if (this.guests.length === 0) {
      this.guestsEnabled = false;
    }
  }


  calculateDuration(start: string, end: string): string {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);

    let minutes = (endH * 60 + endM) - (startH * 60 + startM);
    if (minutes < 0) minutes += 24 * 60;

    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hrs > 0) return `${hrs}h ${mins}mn`;
    return `${mins}mn`;
  }

  enableGuests() {
    this.guestsEnabled = true;
    this.guests = [
      { guestName: '', guestEmail: '', guestId: uuidv4() }
    ];
  }

  isTimeRangeValid(): boolean {
    if (!this.startTime || !this.endTime) return false;

    const [startH, startM] = this.startTime.split(':').map(Number);
    const [endH, endM] = this.endTime.split(':').map(Number);

    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    return startMinutes < endMinutes;
  }

  disableGuests() {
    this.guestsEnabled = false;
    this.guests = [];
    this.confirm(false);
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }


  ngOnInit(): void {
    this.minDate.setDate(this.minDate.getDate() + 1);
  }

  protected readonly ButtonState = ButtonState;

  protected readonly ButtonSize = ButtonSize;

  protected readonly ButtonType = ButtonType;
}
